import React, { useState } from 'react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { Check, Palette } from 'lucide-react';
import { CLOCK_COLOR_THEMES, CLOCK_COLOR_LABELS } from './SobrietyClock';

/**
 * Colour-swatch picker for the dashboard Sobriety Clock box.  Renders
 * each preset as a live-preview button; clicking one calls PATCH
 * /api/auth/update-clock-color and refreshes the auth context so the
 * dashboard picks up the new theme immediately.
 *
 * Deliberately preset-only (no free hex input) so users can't
 * accidentally render the box unreadable by choosing black-on-black.
 */
function SobrietyClockColorPicker({ user, onUpdated }) {
  const [saving, setSaving] = useState(null); // theme key currently saving
  const current = user?.sobriety_clock_color || 'default';

  const pick = async (theme) => {
    if (saving || theme === current) return;
    setSaving(theme);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/update-clock-color`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ color: theme }),
        }
      );
      if (!res.ok) throw new Error('Save failed');
      toast.success('Clock colour updated');
      if (typeof onUpdated === 'function') { try { await onUpdated(); } catch { /* non-fatal */ } }
    } catch (e) {
      toast.error(e.message || 'Could not update colour');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="mt-6" data-testid="clock-color-picker">
      <div className="flex items-center gap-2 mb-2">
        <Palette className="w-4 h-4 text-zinc-600" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-600">Sobriety-clock colour</h3>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        {Object.keys(CLOCK_COLOR_THEMES).map((key) => {
          const isActive = current === key;
          const isSaving = saving === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => pick(key)}
              disabled={!!saving}
              title={CLOCK_COLOR_LABELS[key]}
              aria-label={`Set clock colour to ${CLOCK_COLOR_LABELS[key]}`}
              aria-pressed={isActive}
              className={`relative aspect-square rounded-lg ${CLOCK_COLOR_THEMES[key]} transition-transform ${
                isActive ? 'ring-2 ring-zinc-900 scale-105' : 'hover:scale-105'
              } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid={`clock-color-${key}`}
            >
              {isActive && (
                <Check className="w-4 h-4 text-white drop-shadow absolute inset-0 m-auto" />
              )}
              {isSaving && (
                <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white">…</span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">
        Preview colour applied to the Sobriety Clock box on your dashboard. Text stays white for readability.
      </p>
    </div>
  );
}

export default SobrietyClockColorPicker;
